/*
 * src/config/session.ts
 * 
 * Session management configuration
 * Adjust these values based on your security requirements
 */

export interface SessionConfig {
  // Idle timeout: Auto-logout after this much inactivity
  IDLE_TIMEOUT: number;
  
  // Absolute session limit: Maximum session duration regardless of activity
  MAX_SESSION_DURATION: number;
  
  // Disable absolute session limit - users can stay logged in indefinitely as long as they're active
  DISABLE_MAX_SESSION_DURATION: boolean;
  
  // Warning time: Show warning this long before timeout
  WARNING_TIME: number;
  
  // Token check interval: How often to validate JWT tokens
  TOKEN_CHECK_INTERVAL: number;
  
  // Activity events to track for resetting idle timer
  ACTIVITY_EVENTS: readonly string[];
  
  // Buffer time for token expiration checking
  TOKEN_EXPIRY_BUFFER: number;
}

// Default configuration - adjust these values as needed
export const DEFAULT_SESSION_CONFIG: SessionConfig = {
  // 20 minutes of inactivity
  IDLE_TIMEOUT: 20 * 60 * 1000,
  
  // 8 hours maximum session duration
  MAX_SESSION_DURATION: 8 * 60 * 60 * 1000,
  
  // Set to false to enable the maximum session duration
  DISABLE_MAX_SESSION_DURATION: false,
  
  // 5 minutes warning before timeout
  WARNING_TIME: 5 * 60 * 1000,
  
  // Check token validity every 5 minutes
  TOKEN_CHECK_INTERVAL: 5 * 60 * 1000,
  
  // Events that count as user activity
  ACTIVITY_EVENTS: [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'focus',
  ],
  
  // Show token expiry warning 5 minutes before actual expiration
  TOKEN_EXPIRY_BUFFER: 5 * 60 * 1000,
};

// Development configuration - shorter timeouts for easier testing
export const DEVELOPMENT_SESSION_CONFIG: SessionConfig = {
  ...DEFAULT_SESSION_CONFIG,
  IDLE_TIMEOUT: 20 * 60 * 1000,         // 20 min idle
  MAX_SESSION_DURATION: 8 * 60 * 60 * 1000, // 8 hours
  DISABLE_MAX_SESSION_DURATION: false,
  WARNING_TIME: 2 * 60 * 1000,          // 2 min warning
  TOKEN_CHECK_INTERVAL: 5 * 60 * 1000,  // check every 5 min
};

// Staging configuration - same as development for easier testing
export const STAGING_SESSION_CONFIG: SessionConfig = {
  ...DEVELOPMENT_SESSION_CONFIG,
  // Use same timeouts as development
};

// Production configuration - more secure timeouts
export const PRODUCTION_SESSION_CONFIG: SessionConfig = {
  ...DEFAULT_SESSION_CONFIG,
  // 30 minutes of inactivity 
  IDLE_TIMEOUT: 30 * 60 * 1000,
  // 10 hours maximum session
  MAX_SESSION_DURATION: 10 * 60 * 60 * 1000,
  // Enable max session duration in production for security
  DISABLE_MAX_SESSION_DURATION: false,
  // 5 minutes warning (increased from 2 minutes)
  WARNING_TIME: 5 * 60 * 1000,
};

// Get configuration based on environment
// Uses same logic as gatsby-config.ts for consistency
export const getSessionConfig = (): SessionConfig => {
  const env = process.env.GATSBY_ENV || process.env.NODE_ENV || 'development';
  
  switch (env) {
    case 'development':
      return DEVELOPMENT_SESSION_CONFIG;
    case 'staging':
      // Use same config as development for easier testing
      return STAGING_SESSION_CONFIG;
    case 'production':
      return PRODUCTION_SESSION_CONFIG;
    default:
      console.warn(`🔧 [session.ts] Unknown environment: ${env}, falling back to default`);
      return DEFAULT_SESSION_CONFIG;
  }
};

// Helper functions for time formatting
export const formatDuration = (milliseconds: number): string => {
  const minutes = Math.floor(milliseconds / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }
  
  return `${minutes}m`;
};

export const formatTimeRemaining = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${seconds}s`;
}; 